// src/pages/VehicleV2/VehicleBataDetails.jsx
// Design aligned with ProductionBOMListing
// Flow aligned with VehicleBataDetials.aspx
//
// Backend:
//   app.use("/api/vehicleBetaDetails", require("./routes/vehicleBataDetails"));
//   (or /api/vehicleBataDetails — keep API_BASE in sync with the mount)

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
  FaFileExport,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleBetaDetails`;

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const toStr = (v, d = "") => {
  if (v === null || v === undefined) return d;
  return String(v).trim();
};

const toInt = (v, d = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : d;
};

const emptyFilters = {
  fromDate: "",
  toDate: "",
  storeId: "",
  supplier: "",
  vehicleNumber: "",
  driverName: "",
};

function ymdToDate(ymd) {
  if (!ymd) return null;
  const [y, m, d] = String(ymd).split("-").map((n) => Number(n));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function checkFromTo(fromDate, toDate) {
  if (!fromDate || !toDate) return true;
  const fromD = ymdToDate(fromDate);
  const toD = ymdToDate(toDate);
  if (!fromD || !toD) return false;
  return toD >= fromD;
}

const VehicleBataDetails = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("Branchid") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserIdLS =
    (typeof window !== "undefined" && sessionStorage.getItem("loginUserId")) ||
    userIdLS;

  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(
    () => toStr(branchTypeLS || "HO"),
    [branchTypeLS]
  );
  const userId = useMemo(() => toStr(userIdLS), [userIdLS]);
  const loginUserId = useMemo(
    () => toStr(loginUserIdLS || userId),
    [loginUserIdLS, userId]
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [vehicleType, setVehicleType] = useState("own");
  const [filters, setFilters] = useState(emptyFilters);

  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [pageTotal, setPageTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [vehicleSuggestions, setVehicleSuggestions] = useState([]);
  const [driverSuggestions, setDriverSuggestions] = useState([]);
  const [showVehicleSug, setShowVehicleSug] = useState(false);
  const [showDriverSug, setShowDriverSug] = useState(false);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const vehicleTypeRef = useRef(vehicleType);
  vehicleTypeRef.current = vehicleType;
  const skipTypeEffect = useRef(true);
  const vehicleTimer = useRef(null);
  const driverTimer = useRef(null);

  const totalPages = useMemo(() => {
    const t = toInt(total, 0);
    const l = toInt(limit, 10);
    return Math.max(1, Math.ceil(t / l));
  }, [total, limit]);

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const loadInit = useCallback(async () => {
    try {
      if (!branchId) return;
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId,
          loginUserId: toInt(loginUserId),
          branchType,
        },
        headers: commonHeaders,
      });
      if (data?.success) {
        setStores(Array.isArray(data.stores) ? data.stores : []);
        setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
      } else {
        toast.error(data?.message || "Init failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init error");
    }
  }, [branchId, loginUserId, branchType, commonHeaders]);

  const loadList = useCallback(
    async (opts = {}) => {
      const f = opts.filters ?? filtersRef.current;
      const vType = opts.vehicleType ?? vehicleTypeRef.current;
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;

      if (!branchId) {
        toast.error("BranchId missing!");
        return;
      }

      if (!checkFromTo(f.fromDate, f.toDate)) {
        toast.error("Please select to date greater than from date");
        return;
      }

      setLoading(true);
      setHasSearched(true);

      try {
        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            branchId: toInt(branchId),
            branchType: toStr(branchType),
            loginUserId: toInt(loginUserId),
            vehicleType: vType,
            fromDate: f.fromDate || "",
            toDate: f.toDate || "",
            storeId: f.storeId ? toInt(f.storeId) : "",
            supplier: vType === "hire" ? toStr(f.supplier) : "",
            vehicleNumber: toStr(f.vehicleNumber),
            driverName: toStr(f.driverName),
            page: nextPage,
            limit: nextLimit,
          },
          headers: commonHeaders,
        });

        if (data?.success) {
          const list = Array.isArray(data.list) ? data.list : [];
          setRows(list);
          setTotal(toInt(data.total, 0));
          setGrandTotal(Number(data.grandTotal || 0));
          setPageTotal(Number(data.pageTotal || 0));
          setPage(toInt(data.page, nextPage));
          setLimit(toInt(data.limit, nextLimit));
          if (list.length === 0) toast.info("No Records found");
        } else {
          setRows([]);
          setTotal(0);
          setGrandTotal(0);
          setPageTotal(0);
          toast.info(data?.message || "No Records found");
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        setTotal(0);
        setGrandTotal(0);
        setPageTotal(0);
        toast.error(
          err?.response?.data?.message || err.message || "Search failed"
        );
      } finally {
        setLoading(false);
      }
    },
    [branchId, branchType, loginUserId, commonHeaders, page, limit]
  );

  useEffect(() => {
    loadInit();
    if (branchId && loginUserId) {
      loadList({ page: 1, filters: emptyFilters, vehicleType: "own" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, loginUserId, branchType]);

  useEffect(() => {
    if (skipTypeEffect.current) {
      skipTypeEffect.current = false;
      return;
    }

    setFilters((p) => ({
      ...p,
      supplier: vehicleType === "own" ? "" : p.supplier,
    }));
    setPage(1);
    setVehicleSuggestions([]);
    setDriverSuggestions([]);

    if (branchId && loginUserId) {
      loadList({
        page: 1,
        vehicleType,
        filters: {
          ...filtersRef.current,
          supplier: vehicleType === "own" ? "" : filtersRef.current.supplier,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleType]);

  const fetchVehicleSuggestions = useCallback(
    async (text) => {
      const prefix = toStr(text);
      if (!prefix || !branchId || !loginUserId) {
        setVehicleSuggestions([]);
        return;
      }
      try {
        const { data } = await axios.get(`${API_BASE}/autocomplete/vehicle`, {
          params: {
            branchId,
            branchType,
            loginUserId: toInt(loginUserId),
            vehicleType: vehicleTypeRef.current === "hire" ? "H" : "O",
            storeId: filtersRef.current.storeId || "",
            q: prefix,
          },
          headers: commonHeaders,
        });
        setVehicleSuggestions(
          Array.isArray(data?.list) ? data.list.slice(0, 10) : []
        );
      } catch (err) {
        console.error(err);
        setVehicleSuggestions([]);
      }
    },
    [branchId, branchType, loginUserId, commonHeaders]
  );

  const fetchDriverSuggestions = useCallback(
    async (text) => {
      const prefix = toStr(text);
      if (!prefix || !branchId || !loginUserId) {
        setDriverSuggestions([]);
        return;
      }
      try {
        const { data } = await axios.get(`${API_BASE}/autocomplete/driver`, {
          params: {
            branchId,
            branchType,
            loginUserId: toInt(loginUserId),
            storeId: filtersRef.current.storeId || "",
            q: prefix,
          },
          headers: commonHeaders,
        });
        setDriverSuggestions(
          Array.isArray(data?.list) ? data.list.slice(0, 10) : []
        );
      } catch (err) {
        console.error(err);
        setDriverSuggestions([]);
      }
    },
    [branchId, branchType, loginUserId, commonHeaders]
  );

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      let next = value;

      if (name === "vehicleNumber") {
        next = String(value).replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 10);
      }
      if (name === "driverName") {
        next = String(value).replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 50);
      }

      setFilters((p) => ({ ...p, [name]: next }));

      if (name === "vehicleNumber") {
        clearTimeout(vehicleTimer.current);
        if (!next) {
          setVehicleSuggestions([]);
          setShowVehicleSug(false);
          return;
        }
        vehicleTimer.current = setTimeout(() => {
          fetchVehicleSuggestions(next);
          setShowVehicleSug(true);
        }, 300);
      }

      if (name === "driverName") {
        clearTimeout(driverTimer.current);
        if (!next) {
          setDriverSuggestions([]);
          setShowDriverSug(false);
          return;
        }
        driverTimer.current = setTimeout(() => {
          fetchDriverSuggestions(next);
          setShowDriverSug(true);
        }, 300);
      }
    },
    [fetchVehicleSuggestions, fetchDriverSuggestions]
  );

  const handleSearch = useCallback(() => {
    if (!checkFromTo(filtersRef.current.fromDate, filtersRef.current.toDate)) {
      toast.error("Please select to date greater than from date");
      return;
    }
    setPage(1);
    loadList({ page: 1 });
  }, [loadList]);

  const handleClearFilters = useCallback(() => {
    setFilters(emptyFilters);
    setVehicleSuggestions([]);
    setDriverSuggestions([]);
    setPage(1);
    loadList({ page: 1, filters: emptyFilters });
  }, [loadList]);

  const onExport = useCallback(() => {
    try {
      if (!rows?.length) {
        toast.info("No rows to export");
        return;
      }
      const header = [
        "S.No",
        "Date",
        "Vehicle Number",
        "Vehicle Name",
        "Driver",
        "Supplier",
        "Amount",
      ];
      const lines = rows.map((r, i) => [
        i + 1 + (page - 1) * limit,
        r.date ?? "",
        r.number ?? "",
        r.name ?? "",
        r.driver ?? "",
        r.supplier ?? "",
        Number(r.amount ?? 0).toFixed(2),
      ]);
      const csv = [header, ...lines]
        .map((arr) =>
          arr.map((x) => `"${String(x ?? "").replaceAll('"', '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vehicle_bata_details_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows, page, limit]);

  const goPrev = () => {
    const p = Math.max(1, page - 1);
    setPage(p);
    loadList({ page: p });
  };

  const goNext = () => {
    const p = Math.min(totalPages, page + 1);
    setPage(p);
    loadList({ page: p });
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE BATA DETAILS</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Bata Details
              </h2>
              <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                INFRA SCREEN : B-19
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={onExport}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaFileExport />
                Export
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                  Search Criteria
                </h3>

                <div className="flex gap-2">
                  <button
                    onClick={handleClearFilters}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                      isDark
                        ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-white"
                    }`}
                  >
                    Clear
                  </button>

                  <button
                    onClick={handleSearch}
                    disabled={loading}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="vehicleType"
                      value="own"
                      checked={vehicleType === "own"}
                      onChange={(e) => setVehicleType(e.target.value)}
                      className="form-radio text-blue-600"
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Own Vehicle
                    </span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="vehicleType"
                      value="hire"
                      checked={vehicleType === "hire"}
                      onChange={(e) => setVehicleType(e.target.value)}
                      className="form-radio text-blue-600"
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Hire Vehicle
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleChange}
                    className={fieldClass}
                  >
                    <option value="">Select</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Supplier</label>
                  <select
                    name="supplier"
                    value={filters.supplier}
                    onChange={handleChange}
                    className={fieldClass}
                    disabled={vehicleType === "own"}
                  >
                    <option value="">
                      {vehicleType === "own" ? "Disabled for Own" : "Select"}
                    </option>
                    {suppliers.map((x, idx) => (
                      <option key={`${x.id}-${idx}`} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="relative">
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    name="vehicleNumber"
                    value={filters.vehicleNumber}
                    onChange={handleChange}
                    onFocus={() => vehicleSuggestions.length && setShowVehicleSug(true)}
                    onBlur={() => setTimeout(() => setShowVehicleSug(false), 150)}
                    className={fieldClass}
                    placeholder="Type reg no..."
                    maxLength={10}
                    autoComplete="off"
                  />
                  {showVehicleSug && vehicleSuggestions.length > 0 && (
                    <ul
                      className={`absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border shadow ${
                        isDark
                          ? "bg-gray-800 border-gray-600 text-white"
                          : "bg-white border-gray-200 text-gray-900"
                      }`}
                    >
                      {vehicleSuggestions.map((item) => (
                        <li key={item}>
                          <button
                            type="button"
                            className={`w-full text-left px-3 py-2 ${
                              isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setFilters((p) => ({ ...p, vehicleNumber: item }));
                              setVehicleSuggestions([]);
                              setShowVehicleSug(false);
                            }}
                          >
                            {item}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="relative">
                  <label className={labelClass}>Driver Name</label>
                  <input
                    name="driverName"
                    value={filters.driverName}
                    onChange={handleChange}
                    onFocus={() => driverSuggestions.length && setShowDriverSug(true)}
                    onBlur={() => setTimeout(() => setShowDriverSug(false), 150)}
                    className={fieldClass}
                    placeholder="Type driver..."
                    maxLength={50}
                    autoComplete="off"
                  />
                  {showDriverSug && driverSuggestions.length > 0 && (
                    <ul
                      className={`absolute z-20 mt-1 w-full max-h-48 overflow-auto rounded-lg border shadow ${
                        isDark
                          ? "bg-gray-800 border-gray-600 text-white"
                          : "bg-white border-gray-200 text-gray-900"
                      }`}
                    >
                      {driverSuggestions.map((item) => (
                        <li key={item}>
                          <button
                            type="button"
                            className={`w-full text-left px-3 py-2 ${
                              isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setFilters((p) => ({ ...p, driverName: item }));
                              setDriverSuggestions([]);
                              setShowDriverSug(false);
                            }}
                          >
                            {item}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {loading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {hasSearched ? "No Records found" : "Please search to view records"}
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Number</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Driver</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Supplier</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {rows.map((r, idx) => {
                    const amount = Number(r.amount ?? 0);
                    return (
                      <tr
                        key={`${r.id || r.number}-${idx}`}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {idx + 1 + (page - 1) * limit}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.date || "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {r.number || "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>
                          {r.name || "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.driver || "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.supplier || "-"}
                        </td>
                        <td className={`px-6 py-4 text-sm text-right font-semibold ${isDark ? "text-green-400" : "text-green-600"}`}>
                          {Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className={isDark ? "bg-gray-700 text-white" : "bg-blue-50 text-gray-900"}>
                    <td colSpan="6" className="px-6 py-4 text-right text-sm font-semibold">
                      Grand Total
                    </td>
                    <td className={`px-6 py-4 text-right text-sm font-bold ${isDark ? "text-green-300" : "text-green-700"}`}>
                      {Number(grandTotal || 0).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Page <b>{page}</b> of <b>{totalPages}</b> • Total: <b>{total}</b>
              {rows.length > 0 && (
                <span>
                  {" "}
                  • Page total: <b>{Number(pageTotal || 0).toFixed(2)}</b>
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={goPrev}
                disabled={loading || page <= 1}
                className={`px-3 py-2 rounded-lg border ${
                  isDark
                    ? "border-gray-600 text-white bg-gray-700 disabled:opacity-50"
                    : "border-gray-300 text-gray-700 bg-white disabled:opacity-50"
                }`}
              >
                <FaChevronLeft />
              </button>

              <select
                value={limit}
                onChange={(e) => {
                  const newLimit = toInt(e.target.value, 10);
                  setLimit(newLimit);
                  setPage(1);
                  loadList({ page: 1, limit: newLimit });
                }}
                className={fieldClass}
                style={{ width: 130 }}
                disabled={loading}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                onClick={goNext}
                disabled={loading || page >= totalPages}
                className={`px-3 py-2 rounded-lg border ${
                  isDark
                    ? "border-gray-600 text-white bg-gray-700 disabled:opacity-50"
                    : "border-gray-300 text-gray-700 bg-white disabled:opacity-50"
                }`}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleBataDetails;